/**
 * Type 2 VedaOnline simulator URL — fetch model results from VO DB.
 *
 * Supported shapes (same keys, do not use with ?scenario= mix JSON):
 * 1. Flat query params (preferred for dev / readable links)
 * 2. ?veda=<JSON> (VO-generated links)
 *
 * Scenario identity: prefer `scen` (dim description, e.g. base-$600-Y-100%);
 * API resolves to TIMES `scenario` name (vs~00xx). Legacy `scenario` still accepted.
 */

export const VEDA_LOOKUP_QUERY_KEY = 'veda';

/** Flat param names (also accepted inside ?veda= JSON). */
export const VEDA_FLAT_PARAM_KEYS = [
  'userName',
  'folderName',
  'study',
  'scen',
  'scenario',
  'year',
  'region',
];

/**
 * @param {string} [search]
 * @param {{ defaultRegion?: string }} [options]  region from /country/:iso when omitted
 * @returns {object|null}
 */
export function parseVedaLookupFromSearch(search, options = {}) {
  if (!search || typeof search !== 'string') return null;
  const qs = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(qs);

  const rawJson = params.get(VEDA_LOOKUP_QUERY_KEY);
  if (rawJson?.trim()) {
    try {
      const parsed = JSON.parse(decodeURIComponent(rawJson));
      if (parsed && typeof parsed === 'object') {
        const normalized = normalizeVedaLookup(parsed, options);
        if (normalized) return normalized;
      }
    } catch {
      /* fall through to flat params */
    }
  }

  const flat = {
    userName: pickParam(params, 'userName', 'user_name'),
    folderName: pickParam(params, 'folderName', 'folder_name'),
    scen: pickParam(params, 'scen'),
    scenario: pickParam(params, 'scenario', 'modelScenario'),
    year: pickParam(params, 'year', 'period'),
    region: pickParam(params, 'region', 'iso') || options.defaultRegion,
    studyName: pickParam(params, 'studyName', 'study_name', 'study'),
    weatherYear: pickParam(params, 'weatherYear'),
    supplyWeatherYear: pickParam(params, 'supplyWeatherYear'),
    demandTwh: pickParam(params, 'demandTwh'),
  };

  return normalizeVedaLookup(flat, options);
}

/**
 * @param {object} raw
 * @param {{ defaultRegion?: string }} [options]
 * @returns {object|null}
 */
export function normalizeVedaLookup(raw, options = {}) {
  const userName = pickStr(raw, 'userName', 'user_name');
  const folderName = pickStr(raw, 'folderName', 'folder_name');
  const scen = pickStr(raw, 'scen');
  const scenario = pickStr(raw, 'scenario', 'modelScenario');
  const year = pickStr(raw, 'year', 'period');
  const region = pickStr(raw, 'region', 'iso') || options.defaultRegion;
  const studyName = pickStr(raw, 'studyName', 'study_name', 'study');

  if (!userName || !folderName || (!scen && !scenario) || !year || !region || !studyName) {
    return null;
  }

  const out = {
    userName,
    folderName,
    year,
    region: region.toUpperCase(),
    studyName,
  };

  // Prefer scen when both present (API resolves name for USP / lma_dts).
  if (scen) {
    out.scen = scen;
  } else {
    out.scenario = scenario;
  }

  const weatherYear = pickNum(raw, 'weatherYear');
  const supplyWeatherYear = pickNum(raw, 'supplyWeatherYear');
  const demandTwh = pickNum(raw, 'demandTwh');

  if (weatherYear !== undefined) out.weatherYear = weatherYear;
  if (supplyWeatherYear !== undefined) out.supplyWeatherYear = supplyWeatherYear;
  if (demandTwh !== undefined) out.demandTwh = demandTwh;

  return out;
}

/** True when ?veda= / flat VO params include an explicit demandTwh override. */
export function vedaLookupHasExplicitDemandTwh(lookup) {
  if (!lookup || typeof lookup !== 'object') return false;
  const d = lookup.demandTwh;
  if (d === undefined || d === null || d === '') return false;
  const n = typeof d === 'number' ? d : parseFloat(d);
  return Number.isFinite(n) && n > 0 && n <= 99999;
}

/** VO path will supply demand from report varbls — defer default profile fetch until then. */
export function vedaLookupExpectsReportDemand(lookup) {
  return Boolean(lookup) && !vedaLookupHasExplicitDemandTwh(lookup);
}

/**
 * Dev reference case — himanshu / IND kan10 / TS108 / scen→vs~0033 / 2050.
 * scen resolves via report.scenario_tbl_with_dim_description.name.
 */
export const DEV_VEDA_LOOKUP_TS108 = {
  userName: 'himanshu',
  folderName: 'vervestacks_models~~IND_grids_kan10~~akanudia',
  studyName: 'TS108',
  scen: 'base-$600-Y-100%',
  year: '2050',
  region: 'IND',
};

/**
 * Build readable flat query string (same params the capacity-mix API expects).
 * @param {object} lookup
 * @param {{ debug?: boolean, omitRegion?: boolean }} [options]
 */
export function buildVedaLookupQueryString(lookup, { debug = false, omitRegion = false } = {}) {
  const normalized = normalizeVedaLookup(lookup);
  if (!normalized) {
    throw new Error(
      'Invalid veda lookup — required: userName, folderName, study, scen|scenario, year, region'
    );
  }
  const params = new URLSearchParams();
  params.set('userName', normalized.userName);
  params.set('folderName', normalized.folderName);
  params.set('study', normalized.studyName);
  if (normalized.scen) {
    params.set('scen', normalized.scen);
  } else if (normalized.scenario) {
    params.set('scenario', normalized.scenario);
  }
  params.set('year', normalized.year);
  if (!omitRegion) params.set('region', normalized.region);
  if (normalized.weatherYear !== undefined) {
    params.set('weatherYear', String(normalized.weatherYear));
  }
  if (normalized.supplyWeatherYear !== undefined) {
    params.set('supplyWeatherYear', String(normalized.supplyWeatherYear));
  }
  if (normalized.demandTwh !== undefined) {
    params.set('demandTwh', String(normalized.demandTwh));
  }
  if (debug) params.set('vedaDebug', '1');
  return params.toString();
}

/**
 * @param {string} [isoCode]
 * @param {{ debug?: boolean }} [options]
 */
export function buildDevSimulatorVedaUrl(isoCode = 'IND', options = {}) {
  const region = isoCode.toUpperCase();
  const qs = buildVedaLookupQueryString({ ...DEV_VEDA_LOOKUP_TS108, region }, options);
  return `/country/${region}/simulator?${qs}`;
}

function pickParam(params, ...keys) {
  for (const k of keys) {
    const v = params.get(k);
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '';
}

function pickStr(obj, ...keys) {
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '';
}

function pickNum(obj, key) {
  const v = obj[key];
  if (v === undefined || v === null || v === '') return undefined;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Build raw scenario object for tech mapping from capacity-mix API result.
 */
export function buildVedaRawScenarioFromCapacityMix(apiResult, lookup) {
  const capacity = { ...(apiResult?.capacity || {}) };
  const fromReport = apiResult?.meta?.capacitySource === 'report';

  if (!fromReport) {
    const totals = apiResult?.totals || {};
    if (totals.solarGw > 0) capacity.Solar = totals.solarGw;
    if (totals.windOnGw > 0) capacity.Wind_onshore = totals.windOnGw;
    if (totals.windOffGw > 0) capacity.Wind_offshore = totals.windOffGw;
  }

  const payload = {
    format: 'raw',
    capacity,
  };

  if (lookup?.weatherYear !== undefined) payload.weatherYear = lookup.weatherYear;
  if (lookup?.supplyWeatherYear !== undefined) payload.supplyWeatherYear = lookup.supplyWeatherYear;

  if (lookup?.demandTwh !== undefined) {
    payload.demandTwh = lookup.demandTwh;
  } else {
    const reportDemand = apiResult?.report?.demandTotalTwh;
    if (Number.isFinite(reportDemand) && reportDemand > 0) {
      payload.demandTwh = reportDemand;
    }
  }

  return payload;
}

/** True when VO lookup params are present (flat or ?veda= JSON). */
export function hasVedaLookupInSearch(search, options = {}) {
  return parseVedaLookupFromSearch(search, options) !== null;
}

export function shouldShowVedaDebugPanel(search, options = {}) {
  if (!search || typeof search !== 'string') return false;
  const qs = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(qs);
  const debug = params.get('vedaDebug');
  if (debug === '0' || debug === 'false') return false;
  if (!hasVedaLookupInSearch(search, options)) return false;
  if (debug === '1' || debug === 'true') return true;
  return Boolean(params.get(VEDA_LOOKUP_QUERY_KEY));
}
